// Library methods
import { useEffect, useState, useCallback, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// MUI Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

// Components
import SnackbarMessage from "../../components/UI/SnackbarMessage";
import Loader from "../../components/UI/Loader";
import LanguageSettings from "./LanguageSettings";
import ExamCreationSettings from "./ExamCreationSettings";
import ReportSettings from "./ReportSettings";
import PredefinedExamSettings from "./PredefinedExamSettings";

// Styles
import { PageContainerWrapper } from "../../styles/muiStylesHelper";

// Utilities
import useClinic from "../../hooks/useClinic";
import { allLanguageMap } from "../../i18n/languages";
import { getClinics, updateClinicSettings } from "../../services/Clinic";
import { languageMap } from "../../i18n/languages";
import { ToastContext } from "../../contexts/ToastContext";
import { ExamListProvider } from "../../contexts/ExamListContext";

// constant
const maxColorExamLimit = 20;

const ClinicSettings = () => {
  // internationalization
  const { t, i18n } = useTranslation();

  // navigate
  const navigate = useNavigate();

  // auth0
  const { getAccessTokenSilently } = useAuth0();

  // clinic context
  const { clinicId, setClinicSettings, setClinicLang, setPatientDefaultLang } =
    useClinic();

  const { toast, setToast } = useContext(ToastContext);

  const [isLoadingClinic, setIsLoadingClinic] = useState(true);
  const [currentSettings, setCurrentSettings] = useState({
    // language settings
    clinicDefaultLanguage: { name: "", val: "" },
    patientDefaultLanguage: { name: "", val: "" },

    // report customization settings
    monocularReportPdfLayoutOption: { name: "", val: "" },
    reportDisplayLeftEyeFirst: undefined,

    // exam creation settings
    colorVisionDefaultTimeLimit: undefined,
  });

  const disableLanguageUpdate = useCallback(
    (updatedSettings) => {
      let isIncluded = 0;
      const langKeys = Object.keys(allLanguageMap);
      langKeys.forEach((key) => {
        if (
          t(allLanguageMap[key]?.val) ===
          updatedSettings?.clinicDefaultLanguage?.val
        )
          isIncluded++;
      });
      langKeys.forEach((key) => {
        if (
          t(allLanguageMap[key]?.val) ===
          updatedSettings?.patientDefaultLanguage?.val
        )
          isIncluded++;
      });
      return isIncluded !== 2;
    },
    [t]
  );

  const disableColorVisionUpdate = useCallback(
    (updatedSettings) =>
      updatedSettings?.colorVisionDefaultTimeLimit === undefined ||
      updatedSettings?.colorVisionDefaultTimeLimit === "",
    []
  );

  const disableAutosave = useCallback(
    (updatedSettings) =>
      disableLanguageUpdate(updatedSettings) ||
      disableColorVisionUpdate(updatedSettings),
    [disableLanguageUpdate, disableColorVisionUpdate]
  );

  // confirmation methods
  const handlePortalLanguageConfirmation = useCallback(
    (updatedSettings) => {
      const langKeys = Object.keys(languageMap);
      const clinicLang = langKeys.find(
        (key) =>
          languageMap[key].val === updatedSettings?.clinicDefaultLanguage?.val
      );
      const patientLang = langKeys.find(
        (key) =>
          languageMap[key].val === updatedSettings?.patientDefaultLanguage?.val
      );

      i18n.changeLanguage(clinicLang);
      setClinicLang(clinicLang);
      setPatientDefaultLang(patientLang);
    },
    [i18n, setClinicLang, setPatientDefaultLang]
  );

  const handleUpdateSettings = useCallback(
    async (updatedSettings) => {
      if (disableAutosave(updatedSettings)) return;

      setCurrentSettings((prev) => ({
        ...prev,
        ...updatedSettings,
      }));

      const newClinicSettings = {
        ...updatedSettings,

        // language settings
        clinicDefaultLanguage: updatedSettings?.clinicDefaultLanguage?.val,
        patientDefaultLanguage: updatedSettings?.patientDefaultLanguage?.val,

        // report customization settings
        monocularReportPdfLayoutOption:
          updatedSettings?.monocularReportPdfLayoutOption?.val,
        reportDisplayLeftEyeFirst: updatedSettings?.reportDisplayLeftEyeFirst,

        // exam creation settings
        colorVisionDefaultTimeLimit:
          updatedSettings?.colorVisionDefaultTimeLimit > maxColorExamLimit
            ? maxColorExamLimit * 60
            : updatedSettings?.colorVisionDefaultTimeLimit * 60,
      };

      // add to api
      try {
        // get token
        const token = await getAccessTokenSilently();
        await updateClinicSettings(token, clinicId, newClinicSettings);

        const { settings } = await getClinics(token, clinicId);
        if (settings) setClinicSettings(settings);

        handlePortalLanguageConfirmation(updatedSettings);

        setToast(() => ({
          success: true,
          message: t("settings_success"),
        }));
      } catch (error) {
        setToast(() => ({
          success: false,
          message: t("settings_error"),
        }));
      }
    },
    [
      clinicId,
      disableAutosave,
      handlePortalLanguageConfirmation,
      getAccessTokenSilently,
      setClinicSettings,
      setToast,
      t,
    ]
  );

  // initial data fetching
  const fetchClinicSettings = useCallback(async () => {
    try {
      // get language key from value
      const getObjectKey = (obj, value) => {
        return Object.keys(obj).find((key) => obj[key].val === value);
      };

      // get authToken
      const token = await getAccessTokenSilently();

      const { settings } = await getClinics(token, clinicId);

      if (settings) {
        const clinicDefaultLanguage =
          languageMap[
            getObjectKey(languageMap, settings?.clinicDefaultLanguage)
          ];
        const patientDefaultLanguage =
          allLanguageMap[
            getObjectKey(allLanguageMap, settings.patientDefaultLanguage)
          ];
        const monocularReportPdfLayoutOption = {
          name: settings?.monocularReportPdfLayoutOption
            ? t(
                `settings_monocular_${settings?.monocularReportPdfLayoutOption?.charAt(0)?.toLowerCase() + settings?.monocularReportPdfLayoutOption?.slice(1)}`
              )
            : "",
          val: settings?.monocularReportPdfLayoutOption,
        };
        const reportDisplayLeftEyeFirst = Boolean(
          settings.reportDisplayLeftEyeFirst
        );
        const colorVisionDefaultTimeLimit =
          settings?.colorVisionDefaultTimeLimit / 60.0;

        setCurrentSettings((prev) => ({
          ...(prev ? prev : {}),
          ...settings,
          clinicDefaultLanguage,
          patientDefaultLanguage,
          monocularReportPdfLayoutOption,
          reportDisplayLeftEyeFirst,
          colorVisionDefaultTimeLimit,
        }));

        setClinicSettings(settings);
      }
    } catch (e) {
      console.log(e);
    }
  }, [clinicId, getAccessTokenSilently, setClinicSettings, t]);

  const initialFetch = useCallback(async () => {
    if (!clinicId) navigate("/invalid");
    try {
      setIsLoadingClinic(true);
      await fetchClinicSettings();
      setIsLoadingClinic(false);
    } catch (e) {
      console.log(e);
    }
  }, [navigate, fetchClinicSettings, clinicId]);

  useEffect(() => {
    initialFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        sx={() => PageContainerWrapper()}
        p="24px"
        backgroundColor="#E5E9ED70"
      >
        <Typography fontSize="20px" fontWeight="bold" mb="20px">
          {t("word_clinic_settings")}
        </Typography>
        <Typography fontSize="16px" mb="20px">
          {t("autosave_text")}
        </Typography>
        {isLoadingClinic ? (
          <Loader />
        ) : (
          <Grid
            container
            display="flex"
            width="100%"
            columnGap="32px"
            rowGap="24px"
          >
            <Grid
              container
              item
              xs
              display="flex"
              flexDirection="column"
              gap="24px"
              minWidth={{ xs: "100%", sm: "640px" }}
            >
              <Typography sx={{ textTransform: "uppercase" }} fontSize="16px">
                {t("settings_language_preference")}
              </Typography>
              <Box
                padding="24px"
                borderRadius="16px"
                backgroundColor="#FFF"
                boxShadow="0px 4px 10px rgba(0, 0, 0, 0.15)"
              >
                <LanguageSettings
                  currentSettings={currentSettings}
                  handleUpdateSettings={handleUpdateSettings}
                />
              </Box>
              <Typography sx={{ textTransform: "uppercase" }} fontSize="16px">
                {t("settings_report_customization")}
              </Typography>

              <ReportSettings
                currentSettings={currentSettings}
                handleUpdateSettings={handleUpdateSettings}
              />

              <Typography sx={{ textTransform: "uppercase" }} fontSize="16px">
                {t("settings_exam_creation_customization")}
              </Typography>
              <Box
                padding="24px"
                borderRadius="16px"
                backgroundColor="#FFF"
                boxShadow="0px 4px 10px rgba(0, 0, 0, 0.15)"
              >
                <ExamCreationSettings
                  currentSettings={currentSettings}
                  handleUpdateSettings={handleUpdateSettings}
                />
              </Box>
            </Grid>
            <Grid
              container
              item
              xs
              display="flex"
              flexDirection="column"
              gap="24px"
              minWidth={{ xs: "100%", sm: "640px" }}
            >
              <Typography sx={{ textTransform: "uppercase" }} fontSize="16px">
                {t("settings_exam_package_customization")}
              </Typography>
              <Box
                padding="24px"
                borderRadius="16px"
                backgroundColor="#FFF"
                boxShadow="0px 4px 10px rgba(0, 0, 0, 0.15)"
              >
                <ExamListProvider>
                  <PredefinedExamSettings />
                </ExamListProvider>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* edit language success/failure toasts */}
      <SnackbarMessage
        open={Boolean(toast.message)}
        onClose={() => setToast({ success: false, message: null })}
        success={toast.success}
        autoHideDuration={8000}
        text={toast.message}
      />
    </>
  );
};

export default ClinicSettings;
